import { Button, Chip } from 'tio-ui/components/buttons';
import type { ChipSignature } from 'tio-ui/components/buttons';
import { fn, hash } from '@ember/helper';
import { getProvider, getStatus } from 'tio-common/helpers/observability';
import { Header, Section, VStack } from 'tio-ui/components/layout';
import { LinkTo } from '@ember/routing';
import { Modal, Footer } from 'tio-ui/components/modal';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import AccountTable from 'tio-employee/components/accounts/account-table';
import ChangeActiveAccountModal from 'tio-common/components/account/change-active-account-modal';
import Component from '@glimmer/component';
import didIntersect from 'ember-scroll-modifiers/modifiers/did-intersect';
import embedPdf from 'tio-common/modifiers/embed-pdf';
import pageTitle from 'ember-page-title/helpers/page-title';
import Paginator from 'tio-ui/components/navigation';
import RouterService from '@ember/routing/router-service';
import Table from 'tio-ui/components/table';
import TioPdfModal from 'tio-common/components/tio/pdf-modal';
import transitionTo from 'ember-router-helpers/helpers/transition-to';
import type AccountModel from 'tio-common/models/account';
import type IntlService from 'ember-intl/services/intl';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type ObservableDocumentModel from 'tio-common/models/observable-document';
import type PersonModel from 'tio-common/models/person';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type PartnerService from 'tio-employee/services/partner';
import { PhFileArrowUp, PhEnvelope } from 'ember-phosphor-icons';
import { eq } from 'tio-ui/utilities';
import type ObservableDocumentAdapter from 'tio-employee/adapters/observable-document';

const getStatusIntent = (status: string): ChipSignature['Args']['intent'] | undefined => {
  switch (status) {
    case 'PROCESSING':
      return 'primary';
    case 'INVALID':
    case 'FAILED':
    case 'NOT_APPLIED':
      return 'danger';
    case 'PROCESSED':
    case 'APPLIED':
      return 'success';
    case 'IN_DISPUTE':
    case 'PARTIALLY_APPLIED':
      return 'warning';
  }
};

const getSubmittedIcon = (via: string) => {
  if (via === 'EMAIL') {
    return PhEnvelope;
  }
  return PhFileArrowUp;
};

const getCreatedAt = (document: ObservableDocumentModel) => {
  const createdAtDate = new Date(document.createdAt);
  return createdAtDate.toLocaleString();
};

interface RouteSignature {
  Args: {
    model: PersonModel;
  };
}

export default class AccountActivityAccountRouteComponent extends Component<RouteSignature> {
  @service declare notifications: NotificationsService;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare store: StoreService;
  @service declare partner: PartnerService;

  @tracked showAddLoansModal = false;
  @tracked showChangeAccountModal = false;

  get person() {
    return this.args.model;
  }

  get activeAccounts() {
    return this.person?.activeAccounts || [];
  }

  get isSlr() {
    return this.person.employees.some((employee) => {
      return employee.isActive && !!employee.plan && employee.plan.cadence !== 'NEVER';
    });
  }

  get accountReceivingContributions() {
    if (!this.isSlr) {
      return null;
    }
    return this.activeAccounts.find(
      (account) => account.id === this.person?.accountReceivingContributionsId
    );
  }

  get paidAccounts() {
    return this.person?.paidAccounts || [];
  }

  // gets unpaid accounts that are not receiving contributions
  get otherActiveAccount() {
    return this.activeAccounts
      .filter(
        (account) => !account.isPaidOff && account.id! !== this.accountReceivingContributions?.id
      )
      .sort((a, b) => a.publicOrdinalValue - b.publicOrdinalValue);
  }

  get loans() {
    return this.activeAccounts.map((account) => account.activeLoans).flat() || [];
  }

  get loansTotal() {
    const total = this.loans.reduce((sum, loan) => {
      return (sum += loan.currentBalance);
    }, 0);

    return total.toFixed(2) as unknown as number;
  }

  get showContributionSelector() {
    return this.activeAccounts.length > 1;
  }

  get isWellnessOnly() {
    return (
      this.person.employees.some((employee) => {
        return employee.isActive && !!employee.plan && employee.plan.cadence === 'NEVER';
      }) && !this.isSlr
    );
  }

  get hasSyf() {
    return this.partner.isSyfEnabled;
  }

  get hasPslf() {
    return this.partner.isPslfEnabled;
  }

  get isPslfOnly() {
    return this.hasPslf && !this.isSlr && !this.hasSyf;
  }

  get uploadQueryParams() {
    const params: { source?: string; providers?: string } = {};

    // Determine source (primary product)
    if (this.isSlr) {
      params.source = 'slr';
    } else if (this.hasPslf) {
      params.source = 'pslf';
    } else if (this.hasSyf) {
      params.source = 'syf';
    } else {
      params.source = 'dashboard';
    }

    // Determine providers (document types)
    // PSLF-only or Wellness-only users get nslds,statement
    // All other users get statement,nslds
    if (this.isPslfOnly || this.isWellnessOnly) {
      params.providers = 'nslds,statement';
    } else {
      params.providers = 'statement,nslds';
    }

    return params;
  }

  isReceivingContributions = (account: AccountModel) => {
    return this.person.accountReceivingContributionsId === account.id;
  };

  toggleChangeAccountModal = () => {
    this.showChangeAccountModal = !this.showChangeAccountModal;
  };

  reloadRecords = () => {
    this.router.refresh('authenticated.account-activity.accounts');
  };

  <template>
    {{pageTitle "Student Loan Summary"}}
    <VStack>
      <Header>
        <div class="flex justify-between items-center">
          <p>{{t "accounts.my_accounts"}}</p>
          <Button
            @intent="primary"
            @appearance="outlined"
            {{on
              "click"
              (transitionTo
                "authenticated.observability.upload" (hash queryParams=this.uploadQueryParams)
              )
            }}
          >
            {{t "common.accounts.add_account"}}
          </Button>
        </div>
      </Header>

      <ChangeActiveAccountModal
        @onClose={{this.toggleChangeAccountModal}}
        @show={{this.showChangeAccountModal}}
        @accountOptions={{this.activeAccounts}}
        @accountReceivingContributions={{@model.accountReceivingContributions}}
        @personId={{@model.id}}
        @route="authenticated.account-activity.accounts"
        @onAccountChanged={{this.reloadRecords}}
      />

      {{#if this.accountReceivingContributions}}
        <Section class="overflow-x-auto">
          <:header>
            <span class="font-semibold">{{t "slr.account_receiving_contributions"}}</span>
          </:header>
          <:subHeader>
            <span class="italic">{{t "slr.account_receiving_contributions_sub_header"}}</span>
          </:subHeader>
          <:actions>
            {{#if this.showContributionSelector}}
              <Button
                @intent="primary"
                @appearance="outlined"
                {{on "click" this.toggleChangeAccountModal}}
              >
                {{t "slr.change_contributions"}}
              </Button>
            {{/if}}
          </:actions>
          <:body>
            <AccountTable
              @account={{this.accountReceivingContributions}}
              @isReceivingContributions={{true}}
              @onAccountChanged={{this.reloadRecords}}
            />
          </:body>
        </Section>
      {{/if}}
      {{#if this.otherActiveAccount}}
        <h3 class="font-semibold">{{t "common.accounts.active_accounts"}}</h3>
        {{#each this.otherActiveAccount as |account|}}
          <Section class="overflow-x-auto">
            <:body>
              <AccountTable
                @account={{account}}
                @isReceivingContributions={{false}}
                @onAccountChanged={{this.reloadRecords}}
              />
            </:body>
          </Section>
        {{/each}}
      {{/if}}
      {{! TODO: only show section if there are documents to display }}
      <Section id="my-documents">
        <:header>{{t "accounts.my_documents"}}</:header>
        <:body>
          <DocumentsTable @person={{@model}} />
        </:body>
      </Section>
      {{#if this.paidAccounts.length}}
        <Section>
          <:header>{{t "accounts.paid_in_full"}}</:header>
          <:body>
            <Table>
              <:thead as |options|>
                <options.tr>
                  <options.th>{{t "accounts.servicer"}}</options.th>
                  <options.th>{{t "accounts.number_of_loans"}}</options.th>
                </options.tr>
              </:thead>
              <:tbody as |options|>
                {{#each this.paidAccounts as |account|}}
                  <options.tr>
                    <options.td>{{account.institutionName}}</options.td>
                    <options.td>{{account.activeLoans.length}}</options.td>
                  </options.tr>
                {{/each}}
              </:tbody>
            </Table>
          </:body>
        </Section>
      {{/if}}
    </VStack>
  </template>
}

const PAGE_SIZE = 10;

interface S {
  Args: {
    person: PersonModel;
  };
}
class DocumentsTable extends Component<S> {
  @service declare store: StoreService;
  @service declare notifications: NotificationsService;
  @service declare intl: IntlService;

  @tracked page = 1;
  @tracked limit = PAGE_SIZE;
  @tracked showPreview = false;
  @tracked isInViewport = false;
  @tracked pdfSrc = '';
  @tracked showDeleteModal = false;
  @tracked documentToDelete: ObservableDocumentModel | null = null;

  documentsData = trackedFunction(this, async () => {
    const person = this.args.person;
    const query = {
      filter: {
        person: person.id,
        discarded_at: 'null',
      },
      include: 'observable-asset',
      sort: '-created-at',
      page: { limit: PAGE_SIZE, offset: (this.page - 1) * PAGE_SIZE },
    };

    return (await this.store.query('observable-document', query)) as ObservableDocumentModel[];
  });

  get documents() {
    return this.documentsData?.value || [];
  }

  changePaginatorPage = (page: number) => {
    this.page = page;
  };

  closePreviewModal = () => {
    this.showPreview = false;
  };

  getDocument = async (document: ObservableDocumentModel) => {
    // TODO: Can we query the asset directly?
    const doc = await this.store.findRecord<ObservableDocumentModel>(
      'observable-document',
      // @ts-expect-error: model types
      document.id,
      {
        include: 'observable-asset',
      }
    );
    this.pdfSrc = doc.observableAsset.signedUrl;
    this.showPreview = true;
  };

  deleteDocument = (document: ObservableDocumentModel) => {
    this.documentToDelete = document;
    this.showDeleteModal = true;
  };

  //tbd: convert to shared code in tio-common, clean up tio-admin as well
  confirmDelete = async () => {
    if (!this.documentToDelete || !this.documentToDelete.id) {
      throw new Error('Document ID is missing');
    }

    try {
      const adapter = this.store.adapterFor(
        'observable-document'
      ) as unknown as ObservableDocumentAdapter;
      await adapter.discardObservableDocument(this.documentToDelete.id);

      const record = this.store.peekRecord(
        'observable-document',
        this.documentToDelete.id
      ) as ObservableDocumentModel | null;
      if (record) {
        this.store.unloadRecord(record);
      }

      this.notifications.add(this.intl.t('observability.documents.index.delete_success'), {
        appearance: 'success',
      });
    } catch (e) {
      this.notifications.add(this.intl.t('observability.documents.index.delete_error'), {
        appearance: 'error',
      });
      console.log('Error occurred while deleting document:', e);
    } finally {
      this.showDeleteModal = false;
      this.documentToDelete = null;
    }
  };

  <template>
    <Table {{didIntersect onEnter=(fn (mut this.isInViewport) true)}} @isLoading={{false}}>
      <:thead as |options|>
        <options.tr>
          <options.th>{{t "observability.documents.index.headers.filename"}}</options.th>
          <options.th>{{t "observability.documents.index.headers.provider"}}</options.th>
          <options.th>{{t "observability.documents.index.headers.status"}}</options.th>
          <options.th>{{t "observability.documents.index.headers.submitted"}}</options.th>
          <options.th>{{t "observability.documents.index.headers.action"}}</options.th>
        </options.tr>
      </:thead>
      <:tbody as |options|>
        {{#if this.isInViewport}}
          {{#each this.documents as |document|}}
            <options.tr>
              <options.td>
                <LinkTo
                  @route="authenticated.observability.documents.show"
                  @model={{document}}
                  class="tio-anchor"
                >
                  {{document.observableAsset.filename}}
                </LinkTo>
              </options.td>
              <options.td>
                {{t "observable_document.provider" provider=(getProvider document)}}
              </options.td>
              <options.td>
                <Chip @intent={{getStatusIntent (getStatus document)}}>
                  {{t "observable_document.status" state=(getStatus document)}}
                </Chip>
              </options.td>
              <options.td>
                <div class="flex flex-row flex-nowrap justify-left items-center">

                  {{#let (getSubmittedIcon document.via) as |SubmittedIcon|}}
                    <SubmittedIcon @size="24" class="mr-2" />
                  {{/let}}
                  {{getCreatedAt document}}
                </div>
              </options.td>
              {{#if (eq (getStatus document) "FAILED")}}
                <options.td>
                  <div class="flex gap-x-2">
                    <Button
                      @intent="danger"
                      @description={{t "observability.documents.index.discards_document"}}
                      @showDescription={{true}}
                      {{on "click" (fn this.deleteDocument document)}}
                    >
                      {{t "observability.documents.index.delete_document"}}
                    </Button>
                  </div>
                </options.td>
              {{else}}
                <options.td>
                  {{! Empty cell for non-failed documents to maintain table structure }}
                </options.td>
              {{/if}}
            </options.tr>
          {{/each}}
        {{/if}}
      </:tbody>
      <:tfoot as |options|>
        <options.tr>
          <options.td colspan="4">
            {{! @glint-expect-error}}
            <Paginator @totalItems={{this.documents.meta.page.total}} @pageSize={{PAGE_SIZE}} />
          </options.td>
        </options.tr>
      </:tfoot>
    </Table>
    <TioPdfModal @show={{this.showPreview}} @onClose={{this.closePreviewModal}}>
      {{#if this.pdfSrc}}
        <div {{embedPdf this.pdfSrc}}></div>
      {{/if}}
    </TioPdfModal>
    <Modal
      @isOpen={{this.showDeleteModal}}
      @onClose={{fn (mut this.showDeleteModal) false}}
      @size="sm"
      as |m|
    >
      <m.Header>
        {{t "observability.documents.index.delete_confirm_title"}}
      </m.Header>
      <m.Body>
        {{t "observability.documents.index.delete_confirm_message"}}
      </m.Body>
      <m.Footer>
        <Footer
          @cancelText={{t "common.cancel"}}
          @submitText={{t "common.confirm"}}
          @onCancel={{fn (mut this.showDeleteModal) false}}
          @onSubmit={{this.confirmDelete}}
        />
      </m.Footer>
    </Modal>
  </template>
}
